<script setup lang="ts">
import type { OfferAdditionalOptionsInner } from '~/api/pricing-offers/models/OfferAdditionalOptionsInner'
import { CtIcon } from '@camelot/components'
import { useOfferAdditionalOptions } from '~/composables/offer-additional-options'
import { formatAmountPerDay } from '~/composables/offer-utils'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useExtrasStore } from '~/stores/extras'
import { useOfferStore } from '~/stores/offer'

const extrasStore = useExtrasStore()

const { option: basicOption, shortDescription: basicDescription } = useOfferAdditionalOptions('basic_insurance')
const { option: plusOption, shortDescription: plusDescription } = useOfferAdditionalOptions('plus_insurance')

const plusCoverageChosen = computed<boolean>(() => extrasStore.propertyChosen(plusOption.value?.id))

const option = computed<OfferAdditionalOptionsInner>(() => plusCoverageChosen.value ? plusOption.value! : basicOption.value!)
const description = computed<string>(() => plusCoverageChosen.value ? plusDescription.value : basicDescription.value)

const formattedPricePerDay = computed(() => formatAmountPerDay(
  useTranslations().language.value,
  useOfferStore().selectedOffer,
  option.value?.price,
))
</script>

<template>
  <section class="p-16 pb-32 flex flex-col">
    <h4 class="text-lg text-black-100 flex items-center">
      <CtIcon name="insurance" color="--ct-color-black-100" size="40" class="flex-none mr-8" />
      <span class="text-md">{{ translate('sidebar_coverage_title') }}</span>
    </h4>

    <header class="mt-8 flex justify-between items-center">
      <div class="ml-8 basis-8/12">
        <Transition v-if="option?.name" mode="out-in" name="pulse">
          <p :key="option.name" class="text-black-100">
            {{ option.name }}
          </p>
        </Transition>
        <Transition v-if="option?.name" mode="out-in" name="clock">
          <p :key="option.name" class="mt-4 text-xs leading-normal text-black-80">
            {{ description }}
          </p>
        </Transition>
      </div>

      <Transition mode="out-in" name="fade-bounce">
        <p v-if="!plusCoverageChosen" class="text-right text-mint-80">
          {{ translate('extras_card_included_sub') }}
        </p>
        <ul v-else class="text-right flex flex-col">
          <li>
            {{ option.value }}
          </li>
          <li class="text-xs leading-normal mt-2 text-black-80">
            {{ formattedPricePerDay }}/{{ translate('amount_per_day') }}
          </li>
        </ul>
      </Transition>
    </header>
  </section>
</template>

<style scoped lang="scss">
.fade-bounce-leave-active {
  animation: bounce 0.3s, scale-opacity 0.3s;
}

.fade-bounce-enter-active {
  animation: bounce 0.1s, scale-opacity 0.1s;
}

.fade-bounce-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

@keyframes bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-25px);
  }

  60% {
    transform: translateY(-10px);
  }
}

@keyframes scale-opacity {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.4;
    transform: scale(1.1);
  }
}

.clock-enter-active,
.clock-leave-active {
  transition: transform 0.35s, opacity 0.25s;
}

.clock-enter,
.clock-leave-to {
  opacity: 0;
  transform: translateY(40%);
}

.pulse-enter-active,
.pulse-leave-active {
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.pulse-enter,
.pulse-leave-to {
  opacity: 0;
  transform: scale(1.08);
}
</style>
